import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../services/api';

// Types
export interface CommissionTier {
  id: number;
  name: string;
  minMonthlyRevenue: number;
  maxMonthlyRevenue: number | null;
  commissionPercentage: number;
  description: string;
  active: boolean;
}

export interface PayoutSettings {
  payoutFrequency: string;
  payoutDay: string;
  minimumPayoutAmount: number;
  payoutProcessingDays: number;
  bankTransferFee: number;
  taxPercentage: number;
  gstApplicable: boolean;
  payoutCutoffTime: string;
  payoutCutoffTimezone: string;
}

export interface RevenueSettings {
  taxPercentage: number;
  gstPercentage: number;
  platformFeePercentage: number;
  refundProcessingDays: number;
  chargebackProtectionDays: number;
  currencyCode: string;
  currencySymbol: string;
}

export interface RevenueRecord {
  id: number;
  partnerId: number;
  bookingId: number;
  grossAmount: number;
  platformFee: number;
  commission: number;
  tax: number;
  netAmount: number;
  date: string;
  status: 'completed' | 'pending' | 'refunded';
  commissionTierId: number;
  notes?: string;
}

export interface PayoutRecord {
  id: number;
  partnerId: number;
  payoutAmount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  payoutDate: string;
  completedDate?: string;
  bankAccountId: number;
  transactionId?: string;
  failureReason?: string;
  revenueRecordIds: number[];
  createdAt: string;
  updatedAt: string;
}

export interface BankAccount {
  id: number;
  partnerId: number;
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  accountType: 'savings' | 'current';
  isPrimary: boolean;
  isVerified: boolean;
  verificationStatus: 'pending' | 'verified' | 'failed';
  verificationAttempts: number;
  lastVerificationDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RevenueSummary {
  totalGrossRevenue: number;
  totalCommission: number;
  totalTax: number;
  totalNetRevenue: number;
  averageDailyRevenue: number;
  currentMonthRevenue: number;
  currentTierName: string;
  nextTierName?: string;
  revenueToNextTier?: number;
}

export interface RevenueState {
  // Configuration
  commissionTiers: CommissionTier[];
  payoutSettings: PayoutSettings | null;
  revenueSettings: RevenueSettings | null;

  // Data
  revenueSummary: RevenueSummary | null;
  revenueRecords: RevenueRecord[];
  payoutRecords: PayoutRecord[];
  bankAccounts: BankAccount[];

  // UI State
  isLoading: boolean;
  error: string | null;
  selectedDateRange: {
    startDate: string;
    endDate: string;
  };
  selectedPeriod: 'today' | 'week' | 'month' | 'year' | 'custom';
}

const initialState: RevenueState = {
  commissionTiers: [],
  payoutSettings: null,
  revenueSettings: null,
  revenueSummary: null,
  revenueRecords: [],
  payoutRecords: [],
  bankAccounts: [],
  isLoading: false,
  error: null,
  selectedDateRange: {
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  },
  selectedPeriod: 'month'
};

// Async Thunks
export const fetchCommissionTiers = createAsyncThunk(
  'revenue/fetchCommissionTiers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiClient.get<{ commissionTiers: CommissionTier[] }>('/config/commission-tiers');
      return data.commissionTiers;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch commission tiers');
    }
  }
);

export const fetchPayoutSettings = createAsyncThunk(
  'revenue/fetchPayoutSettings',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiClient.get<{ payoutSettings: PayoutSettings }>('/config/payout-settings');
      return data.payoutSettings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payout settings');
    }
  }
);

export const fetchRevenueSettings = createAsyncThunk(
  'revenue/fetchRevenueSettings',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiClient.get<{ revenueSettings: RevenueSettings }>('/config/revenue-settings');
      return data.revenueSettings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch revenue settings');
    }
  }
);

export const fetchRevenueSummary = createAsyncThunk(
  'revenue/fetchRevenueSummary',
  async (params: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const data = await apiClient.get<RevenueSummary>('/revenue/summary', {
        params
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch revenue summary');
    }
  }
);

export const fetchRevenueRecords = createAsyncThunk(
  'revenue/fetchRevenueRecords',
  async (params: { startDate?: string; endDate?: string; status?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const data = await apiClient.get<{ records: RevenueRecord[]; total: number }>('/revenue/records', {
        params
      });
      return data.records;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch revenue records');
    }
  }
);

export const fetchPayoutRecords = createAsyncThunk(
  'revenue/fetchPayoutRecords',
  async (params: { status?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const data = await apiClient.get<{ records: PayoutRecord[]; total: number }>('/payouts', {
        params
      });
      return data.records;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payout records');
    }
  }
);

export const fetchBankAccounts = createAsyncThunk(
  'revenue/fetchBankAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiClient.get<{ accounts: BankAccount[] }>('/bank-accounts');
      return data.accounts;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bank accounts');
    }
  }
);

export const addBankAccount = createAsyncThunk(
  'revenue/addBankAccount',
  async (account: {
    accountHolderName: string;
    accountNumber: string;
    ifscCode: string;
    bankName: string;
    accountType: 'savings' | 'current';
  }, { rejectWithValue }) => {
    try {
      const data = await apiClient.post<BankAccount>('/bank-accounts', account);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add bank account');
    }
  }
);

export const updateBankAccount = createAsyncThunk(
  'revenue/updateBankAccount',
  async ({ id, account }: { id: number; account: Partial<BankAccount> }, { rejectWithValue }) => {
    try {
      const data = await apiClient.put<BankAccount>(`/bank-accounts/${id}`, account);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update bank account');
    }
  }
);

export const deleteBankAccount = createAsyncThunk(
  'revenue/deleteBankAccount',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/bank-accounts/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete bank account');
    }
  }
);

export const setPrimaryBankAccount = createAsyncThunk(
  'revenue/setPrimaryBankAccount',
  async (id: number, { rejectWithValue }) => {
    try {
      const data = await apiClient.put<BankAccount>(`/bank-accounts/${id}/set-primary`, {});
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set primary bank account');
    }
  }
);

export const requestPayout = createAsyncThunk(
  'revenue/requestPayout',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiClient.post<PayoutRecord>('/payouts/request', {});
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to request payout');
    }
  }
);

// Slice
const revenueSlice = createSlice({
  name: 'revenue',
  initialState,
  reducers: {
    setDateRange: (state, action: PayloadAction<{ startDate: string; endDate: string }>) => {
      state.selectedDateRange = action.payload;
    },
    setPeriod: (state, action: PayloadAction<'today' | 'week' | 'month' | 'year' | 'custom'>) => {
      state.selectedPeriod = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Commission Tiers
    builder
      .addCase(fetchCommissionTiers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCommissionTiers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.commissionTiers = action.payload;
      })
      .addCase(fetchCommissionTiers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Payout Settings
    builder
      .addCase(fetchPayoutSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPayoutSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payoutSettings = action.payload;
      })
      .addCase(fetchPayoutSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Revenue Settings
    builder
      .addCase(fetchRevenueSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRevenueSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.revenueSettings = action.payload;
      })
      .addCase(fetchRevenueSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Revenue Summary
    builder
      .addCase(fetchRevenueSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRevenueSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.revenueSummary = action.payload;
      })
      .addCase(fetchRevenueSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Revenue Records
    builder
      .addCase(fetchRevenueRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRevenueRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.revenueRecords = action.payload;
      })
      .addCase(fetchRevenueRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Payout Records
    builder
      .addCase(fetchPayoutRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPayoutRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payoutRecords = action.payload;
      })
      .addCase(fetchPayoutRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Bank Accounts
    builder
      .addCase(fetchBankAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bankAccounts = action.payload;
      })
      .addCase(fetchBankAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Add Bank Account
    builder
      .addCase(addBankAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addBankAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bankAccounts.push(action.payload);
      })
      .addCase(addBankAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Bank Account
    builder
      .addCase(updateBankAccount.fulfilled, (state, action) => {
        const index = state.bankAccounts.findIndex(acc => acc.id === action.payload.id);
        if (index !== -1) {
          state.bankAccounts[index] = action.payload;
        }
      });

    // Delete Bank Account
    builder
      .addCase(deleteBankAccount.fulfilled, (state, action) => {
        state.bankAccounts = state.bankAccounts.filter(acc => acc.id !== action.payload);
      });

    // Set Primary Bank Account
    builder
      .addCase(setPrimaryBankAccount.fulfilled, (state, action) => {
        state.bankAccounts = state.bankAccounts.map(acc => ({
          ...acc,
          isPrimary: acc.id === action.payload.id
        }));
      });

    // Request Payout
    builder
      .addCase(requestPayout.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestPayout.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payoutRecords.unshift(action.payload);
      })
      .addCase(requestPayout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setDateRange, setPeriod, clearError } = revenueSlice.actions;
export default revenueSlice.reducer;
